import React, { Fragment, useEffect, useState } from "react";
import { Grid2 as Grid } from "@mui/material";
import { DesktopDatePickerField, FormAutoComplete, FormTextField } from "../../../generic/GenericTextFields";
import { GENDER, LINKS_KEY, PROFESSIONS } from "../../../../enums/profileSetupEnums";
import { useFormik } from "formik";
import { object, string, mixed } from "yup";
import { ProfileSetupButtons } from "../ProfileSetupButtons";
import { Country } from "country-state-city";
import { getStatesByCountryName } from "../../../../helpers/countryHelper";

export const BasicInformationForm = ({ handleSubmit, initialUserDetail, handleCloseDialog, submitText, isProfileSetup = true }) => {
  const [states, setStates] = useState([]);
  const [countries, setCountries] = useState([]);
  const formik = useFormik({
    initialValues: {
      name: initialUserDetail?.user?.name ?? "",
      profession: initialUserDetail?.profession ?? "",
      gender: initialUserDetail?.gender ?? "",
      country: initialUserDetail?.country ?? "",
      state: initialUserDetail?.state ?? "",
      ...(!isProfileSetup && { birthday: initialUserDetail?.birthday ?? "", })
    },
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    validationSchema: object().shape({
      'name': string().trim().required('Creator name is required'),
      'profession': string().trim().required('Profession is required'),
      'gender': string().trim().required('Gender is required'),
      'country': mixed().required('Country is required').test('country-label', 'Country is required', val => {
        return typeof val === 'object' ? !!val?.label?.trim() : !!val?.trim?.();
      }),
    }),
    onSubmit: (values) => {
      const formattedValues = {
        ...values,
        country: values?.country?.label || values?.country,
        state: values?.state?.label || values?.state,
        city: values?.city?.label || values?.city
      };
      handleSubmit(formattedValues, LINKS_KEY);
    }
  });

  useEffect(() => {
    const countriesList = Country.getAllCountries();
    setCountries(countriesList);
  }, []);

  useEffect(() => {
    if (formik?.values?.country) {
      if (formik?.values?.country !== initialUserDetail?.country) {
        formik.setFieldValue('state', null);
      }
      const statesList = getStatesByCountryName(formik?.values?.country);
      setStates(statesList);
    } else {
      formik.setFieldValue('state', null);
      setStates([]);
    }
  }, [formik?.values?.country]);

  return (
    <Fragment>
      <Grid container spacing={2}>
        <Grid item size={{ xs: 12, sm: isProfileSetup ? 6 : 12 }}>
          <FormTextField
            value={formik?.values.name}
            placeholder={'Name'}
            label={'Creator Name'}
            error={Boolean(formik?.errors?.name)}
            helperText={formik?.errors?.name}
            handleChange={(value) => formik.setFieldValue('name', value)}
          />
        </Grid>
        <Grid item size={{ xs: 12, sm: 6 }}>
          <FormAutoComplete
            value={formik?.values.profession}
            options={PROFESSIONS}
            label={'Profession'}
            placeholder={'Please Select'}
            error={Boolean(formik?.errors?.profession)}
            helperText={formik?.errors?.profession}
            handleChange={(value) => formik.setFieldValue('profession', value)}
          />
        </Grid>
        {
          !isProfileSetup && <Grid item size={{ xs: 12, sm: 6 }}>
            <DesktopDatePickerField
              disabled={true}
              isRequired={false}
              value={formik?.values?.birthday}
              label={'Birthday'}
              error={Boolean(formik?.errors?.birthday)}
              helperText={formik?.errors?.birthday}
              handleChange={(value) => formik.setFieldValue('birthday', value)}
            />
          </Grid>
        }
        <Grid item size={{ xs: 12, sm: 4 }}>
          <FormAutoComplete
            value={formik?.values.gender}
            options={GENDER}
            label={'Gender'}
            placeholder={'Please Select'}
            error={Boolean(formik?.errors?.gender)}
            helperText={formik?.errors?.gender}
            handleChange={(value) => formik.setFieldValue('gender', value)}
          />
        </Grid>
        <Grid item size={{ xs: 12, sm: 4 }}>
          <FormAutoComplete
            value={formik?.values.country}
            options={countries?.map(country => ({ label: country?.name, value: country?.isoCode }))}
            label={'Country'}
            placeholder={'Please Select'}
            error={Boolean(formik?.errors?.country)}
            helperText={formik?.errors?.country}
            handleChange={(value) => formik.setFieldValue('country', value)}
          />
        </Grid>
        <Grid item size={{ xs: 12, sm: 4 }}>
          <FormAutoComplete
            value={formik?.values.state}
            options={states?.map(state => ({ label: state?.name, value: state?.isoCode }))}
            label={'State/Region'}
            placeholder={'Please Select'}
            error={Boolean(formik?.errors?.state)}
            helperText={formik?.errors?.state}
            handleChange={(value) => formik.setFieldValue('state', value)}
            isRequired={false}
          />
        </Grid>
      </Grid>
      <ProfileSetupButtons submitText={submitText} handleSubmit={() => formik.handleSubmit()} handleBack={handleCloseDialog} />
    </Fragment>
  );
};